import React, { useEffect, useRef } from "react";
import {
  Code,
  Heart,
  Lightbulb,
  Settings,
  Smartphone,
  Users,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

const About: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Create a master timeline for the section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      // Title animation with split text
      if (titleRef.current) {
        const splitTitle = new SplitText(titleRef.current, {
          type: "chars,words",
        });
        tl.from(splitTitle.chars, {
          opacity: 0,
          scale: 0,
          y: 80,
          rotationX: 180,
          transformOrigin: "0% 50% -50",
          stagger: 0.02,
          duration: 0.8,
          ease: "back.out(1.7)",
        });
      }

      // Image animation with 3D effect
      tl.from(
        imageRef.current,
        {
          x: -100,
          opacity: 0,
          rotationY: 45,
          duration: 1.2,
          ease: "power3.out",
        },
        "-=0.4"
      );

      // Text content animation with stagger
      const textElements = textRef.current?.children || [];
      tl.from(
        textElements,
        {
          y: 50,
          opacity: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: "power2.out",
        },
        "-=0.8"
      );

      // Philosophy cards animation with 3D flip
      const cards = document.querySelectorAll(".grid-cols-2 > div");
      tl.from(
        cards,
        {
          y: 30,
          opacity: 0,
          rotationX: 90,
          transformOrigin: "50% 50%",
          duration: 0.8,
          stagger: 0.1,
          ease: "back.out(1.7)",
        },
        "-=0.4"
      );

      // Add hover animations for cards
      cards.forEach((card) => {
        gsap.to(card, {
          scale: 1.05,
          duration: 0.3,
          ease: "power2.out",
          paused: true,
          onStart: () => {
            gsap.to(card.querySelector(".bg-portfolio-blue\\/10"), {
              backgroundColor: "rgba(59, 130, 246, 0.2)",
              duration: 0.3,
            });
          },
          onReverseComplete: () => {
            gsap.to(card.querySelector(".bg-portfolio-blue\\/10"), {
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              duration: 0.3,
            });
          },
        });

        card.addEventListener("mouseenter", () =>
          gsap.to(card, { scale: 1.05, duration: 0.3 })
        );
        card.addEventListener("mouseleave", () =>
          gsap.to(card, { scale: 1, duration: 0.3 })
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="py-20 bg-white">
      <div className="container-custom">
        <h2 ref={titleRef} className="section-title">
          About Me
        </h2>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image Column */}
          <div ref={imageRef} className="relative perspective-1000">
            <div className="relative border-8 border-white shadow-xl max-w-md mx-auto">
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1772&q=80"
                alt="Developer working on code"
                className="w-full h-auto rounded shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-portfolio-blue/10 -z-10"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-portfolio-accent/10 -z-10"></div>
            </div>
          </div>

          {/* Text Content */}
          <div ref={textRef} className="space-y-6">
            <p className="text-lg leading-relaxed text-gray-700">
              I'm Anas Chatouane, a Full Stack Developer from Rabat with over{" "}
              <span className="highlight">4 years of experience</span>. I've
              specialized in React/Next.js, while maintaining strong skills in
              React Native and Expo for mobile development and Laravel for web
              projects.
            </p>

            <p className="text-lg leading-relaxed text-gray-700">
              Currently at Masraf, I'm wearing two hats as both Product Lead and
              Mobile Developer, where I guide a small team building secure
              banking applications. Before this, I worked at companies like
              Bewizyu, SQLI Maroc, and Joodek, tackling everything from medical
              management systems to e-commerce platforms.
            </p>

            <p className="text-lg leading-relaxed text-gray-700">
              I'm particularly good at translating business needs into technical
              solutions and setting up efficient development workflows. What
              drives me is building high-quality software that actually solves
              real problems.
            </p>

            <h3 className="text-xl font-bold text-portfolio-blue mt-8 mb-4">
              My Development Philosophy
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <div className="bg-portfolio-blue/10 p-3 rounded-lg">
                  <Code size={24} className="text-portfolio-blue" />
                </div>
                <div>
                  <h4 className="font-medium">Clean Code</h4>
                  <p className="text-sm text-gray-600">
                    Building maintainable software through thoughtful
                    architecture
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="bg-portfolio-blue/10 p-3 rounded-lg">
                  <Users size={24} className="text-portfolio-blue" />
                </div>
                <div>
                  <h4 className="font-medium">Team Collaboration</h4>
                  <p className="text-sm text-gray-600">
                    Bridging technical and business stakeholders effectively
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="bg-portfolio-blue/10 p-3 rounded-lg">
                  <Smartphone size={24} className="text-portfolio-blue" />
                </div>
                <div>
                  <h4 className="font-medium">Mobile First</h4>
                  <p className="text-sm text-gray-600">
                    Crafting exceptional mobile experiences with React Native
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="bg-portfolio-blue/10 p-3 rounded-lg">
                  <Settings size={24} className="text-portfolio-blue" />
                </div>
                <div>
                  <h4 className="font-medium">Full Stack Expertise</h4>
                  <p className="text-sm text-gray-600">
                    From frontend polish to backend performance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
